import {
  normalizeInvestmentData,
  normalizeInvestmentCalculate,
  normalizePolicyData,
  normalizeContactData,
  normalizeOTSData,
  normalizeWorkshopApplicationData,
  validateInvestmentSubmit,
  validatePolicySubmit,
  validateContactSubmit,
  validateOTSSubmit,
  validateWorkshopApplication,
  formatValidationErrors,
} from '../utils/apiContract';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_URL || 'https://ago-backend.onrender.com')
  : 'http://localhost:5000';

const LEARN_EARN_API = `${API_BASE_URL}/api/learn-earn`;
const CORE_API = `${API_BASE_URL}/api`;

console.log('🔧 API Configuration:', {
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  API_BASE_URL: API_BASE_URL,
  LEARN_EARN_API: LEARN_EARN_API,
  CORE_API: CORE_API,
  timestamp: new Date().toISOString()
});

// Log all environment variables that start with REACT_APP_
console.log('🔧 All REACT_APP_ Environment Variables:',
  Object.keys(process.env)
    .filter(key => key.startsWith('REACT_APP_'))
    .reduce((obj, key) => {
      obj[key] = process.env[key];
      return obj;
    }, {})
);

// Generic API request function with new response format handling
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  // Don't set Content-Type for FormData
  if (options.body instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  try {
    console.log('🔄 API Request:', url, config);

    // Add timeout for server cold starts (30 seconds)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    const response = await fetch(url, {
      ...config,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    console.log('📡 Response received:', {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries())
    });

    // Check if response is ok first
    if (!response.ok) {
      console.error('❌ HTTP Error:', response.status, response.statusText);

      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        throw new Error(`Authentication required (${response.status})`);
      }

      // Try to get error message from response
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          const errorData = await response.json();
          // Handle new error response format with detailed validation errors
          let errorMessage = errorData.message || errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`;
          
          // If there are validation details, include them
          if (errorData.data && errorData.data.details && Array.isArray(errorData.data.details)) {
            const validationErrors = errorData.data.details.map(d => d.message).join('; ');
            errorMessage = `Validation error: ${validationErrors}`;
          }
          
          throw new Error(errorMessage);
        } catch (parseError) {
          console.error('❌ Failed to parse error JSON:', parseError);
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } else {
        // Non-JSON error response
        const text = await response.text();
        console.error('❌ Non-JSON error response:', text);
        throw new Error(`Server error (${response.status}): ${response.statusText}`);
      }
    }

    // Response is OK, now check content type
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('❌ Received non-JSON success response:', text);
      console.error('❌ Content-Type:', contentType);

      // If it's a successful response but not JSON, that's unusual
      throw new Error('Server returned non-JSON response for successful request');
    }

    const responseData = await response.json();
    console.log('✅ API Response:', responseData);

    // Handle new response format
    if (responseData.success === false) {
      const errorMessage = responseData.message || responseData.error?.message || 'API request failed';
      throw new Error(errorMessage);
    }

    // Return the data portion of the response for successful requests
    return {
      success: responseData.success !== false,
      data: responseData.data || responseData,
      message: responseData.message,
      metadata: responseData.metadata
    };

  } catch (error) {
    if (error.name === 'AbortError') {
      console.error('❌ Request timeout (30s) - likely server cold start');
      throw new Error('Request timeout: Server is starting up, please try again in a moment');
    }

    // Check for network errors
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      console.error('❌ Network Error - Backend may be down or CORS issue');
      throw new Error('Network error: Unable to connect to server. Please check your internet connection and try again.');
    }

    console.error('❌ API request failed:', error);
    console.error('❌ Full error details:', {
      message: error.message,
      stack: error.stack,
      url: url,
      options: config
    });
    throw error;
  }
};

// Policy API functions
export const policyAPI = {
  submit: async (policyData) => {
    // Normalize and validate data using API contract
    const normalized = normalizePolicyData(policyData);
    const validation = validatePolicySubmit(normalized);
    
    if (!validation.isValid) {
      const errorMessage = formatValidationErrors(validation.errors);
      console.error('❌ Policy validation failed:', validation.errors);
      throw new Error(`Validation failed:\n${errorMessage}`);
    }

    console.log('✅ Policy data validated and normalized:', normalized);

    const endpoint = `/api/policy/submit`;

    return apiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(normalized)
    });
  },

  getAll: async (page = 1, limit = 10, status = '') => {
    const params = new URLSearchParams({ page, limit });
    if (status) params.append('status', status);

    return apiRequest(`/api/policy/all?${params}`);
  },

  updateStatus: async (id, status, notes = '') => {
    return apiRequest(`/api/policy/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, notes }),
    });
  },
};

// Investment API functions
export const investmentAPI = {
  submit: async (investmentData) => {
    // Normalize and validate data using API contract
    const normalized = normalizeInvestmentData(investmentData);
    const validation = validateInvestmentSubmit(normalized);
    
    if (!validation.isValid) {
      const errorMessage = formatValidationErrors(validation.errors);
      console.error('❌ Investment validation failed:', validation.errors);
      throw new Error(`Validation failed:\n${errorMessage}`);
    }

    console.log('✅ Investment data validated and normalized:', normalized);
    
    return apiRequest('/api/investment/submit', {
      method: 'POST',
      body: JSON.stringify(normalized),
    });
  },

  calculate: async (amount, years, returnRate) => {
    // Backend expects: amount, duration, investmentType
    // Use normalizer to ensure correct format
    const normalized = normalizeInvestmentCalculate({
      amount: amount,
      duration: years,
      investmentType: 'sip'  // Default to SIP for calculator
    });

    return apiRequest('/api/investment/calculate', {
      method: 'POST',
      body: JSON.stringify(normalized),
    });
  },

  getAll: async (page = 1, limit = 10, status = '') => {
    const params = new URLSearchParams({ page, limit });
    if (status) params.append('status', status);

    return apiRequest(`/api/investment/all?${params}`);
  },

  updateStatus: async (id, status, notes = '') => {
    return apiRequest(`/api/investment/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, notes }),
    });
  },

  getPaymentStatus: async (id) => {
    return apiRequest(`/api/investment/${id}/payment-status`);
  },
};

// Contact API functions
export const contactAPI = {
  submit: async (contactData) => {
    // Normalize and validate data using API contract
    const normalized = normalizeContactData(contactData);
    const validation = validateContactSubmit(normalized);
    
    if (!validation.isValid) {
      const errorMessage = formatValidationErrors(validation.errors);
      console.error('❌ Contact validation failed:', validation.errors);
      throw new Error(`Validation failed:\n${errorMessage}`);
    }

    console.log('✅ Contact data validated and normalized:', normalized);
    
    return apiRequest('/api/contact', {
      method: 'POST',
      body: JSON.stringify(normalized),
    });
  },
};

// Learn and Earn API functions - COMPLETELY UPDATED
export const learnEarnAPI = {
  // Customer Work APIs
  customerWork: {
    submit: async (formData) => {
      // Note: FormData validation happens in the component before creating FormData
      // We can't normalize FormData here, but validation should happen before FormData creation
      
      // Use FormData for file upload
      const response = await apiRequest('/api/ots/submit', {
        method: 'POST',
        headers: {}, // Don't set Content-Type, let browser set it for FormData
        body: formData, // FormData object
      });
      return response;
    },

    getMySubmissions: async (email, page = 1, limit = 10) => {
      const params = new URLSearchParams({ email, page, limit });
      return apiRequest(`${LEARN_EARN_API}/customer-work/my-submissions?${params}`);
    },

    checkStatus: async (workId) => {
      return apiRequest(`${LEARN_EARN_API}/customer-work/${workId}/status`);
    },

    // Admin APIs
    getAll: async (page = 1, limit = 50, status = '', workType = '', token) => {
      const params = new URLSearchParams({ page, limit });
      if (status) params.append('status', status);
      if (workType) params.append('workType', workType);

      // Backend route is /api/ots/all not /api/learn-earn/customer-work/all
      return apiRequest(`/api/ots/all?${params}`);
    },

    updateStatus: async (id, status, notes = '', assignedTo = '') => {
      return apiRequest(`/api/learn-earn/customer-work/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status, notes, assignedTo }),
      });
    },

    updateStatus: async (submissionId, statusData, token) => {
      return apiRequest(`${LEARN_EARN_API}/customer-work/${submissionId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: statusData.status, // 'pending', 'in-progress', 'completed'
          notes: statusData.notes,
          assignedTo: statusData.assignedTo
        }),
      });
    },

    downloadFile: async (submissionId, token) => {
      const response = await fetch(`${LEARN_EARN_API}/customer-work/${submissionId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Handle file download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `submission-${submissionId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
      
      return await response.json();
    },
  },

  // Workshop APIs - COMPLETELY NEW
  workshop: {
    getWorkItems: async (page = 1, limit = 20, type = 'assignment') => {
      const params = new URLSearchParams({ page, limit, type });
      return apiRequest(`${LEARN_EARN_API}/workshop/work-items?${params}`);
    },

    submitApplication: async (applicationData) => {
      // Normalize and validate data using API contract
      const normalized = normalizeWorkshopApplicationData(applicationData);
      const validation = validateWorkshopApplication(normalized);
      
      if (!validation.isValid) {
        const errorMessage = formatValidationErrors(validation.errors);
        console.error('❌ Workshop application validation failed:', validation.errors);
        throw new Error(`Validation failed:\n${errorMessage}`);
      }

      console.log('✅ Workshop application validated and normalized:', normalized);
      
      return apiRequest('/api/learn-earn/workshop/apply', {
        method: 'POST',
        body: JSON.stringify(normalized),
      });
    },

    getMyApplications: async (email) => {
      const params = new URLSearchParams({ email });
      return apiRequest(`${LEARN_EARN_API}/workshop/my-applications?${params}`);
    },
  },

  // Subscription Service APIs - COMPLETELY NEW
  subscription: {
    register: async (userData) => {
      console.log('🚀 Subscription Register API - Data being sent:', userData);

      // Clean data - ensure proper types
      const cleanData = {
        name: userData.name.toString(),
        phone: userData.phone.toString(), // Must be 10 digits
        address: userData.address.toString(),
        pincode: userData.pincode.toString(), // Must be 6 digits
        age: parseInt(userData.age), // 13-100
        email: userData.email.toString(),
        password: userData.password.toString(), // Must meet complexity requirements
        studentCategory: userData.studentCategory.toString() // 'school', 'college', 'graduate', 'other'
      };

      console.log('🧹 Clean subscription register data being sent:', cleanData);

      try {
        const response = await fetch(`${LEARN_EARN_API}/subscription/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(cleanData)
        });

        console.log('📡 Subscription register response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ Subscription register error response:', errorText);
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const responseData = await response.json();
        console.log('✅ Subscription register success response:', responseData);

        return {
          success: true,
          data: responseData,
          message: responseData.message
        };

      } catch (error) {
        console.error('❌ Subscription Register API Error:', error);
        throw error;
      }
    },

    login: async (loginData) => {
      console.log('🚀 Subscription Login API - Data being sent:', loginData);

      const cleanData = {
        username: loginData.username.toString(),
        password: loginData.password.toString()
      };

      try {
        const response = await fetch(`${LEARN_EARN_API}/subscription/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(cleanData)
        });

        console.log('📡 Subscription login response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ Subscription login error response:', errorText);
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const responseData = await response.json();
        console.log('✅ Subscription login success response:', responseData);

        return {
          success: true,
          data: responseData,
          message: responseData.message
        };

      } catch (error) {
        console.error('❌ Subscription Login API Error:', error);
        throw error;
      }
    },

    verifyOTP: async (phone, otp) => {
      return apiRequest(`${LEARN_EARN_API}/subscription/verify-otp`, {
        method: 'POST',
        body: JSON.stringify({
          phone: phone,
          otp: otp // 6-digit OTP
        }),
      });
    },

    verifyEmailOTP: async (email, otp) => {
      return apiRequest(`${LEARN_EARN_API}/subscription/verify-email-otp`, {
        method: 'POST',
        body: JSON.stringify({
          email: email,
          otp: otp // 6-digit OTP
        }),
      });
    },

    resendOTP: async (phone) => {
      return apiRequest(`${LEARN_EARN_API}/subscription/resend-otp`, {
        method: 'POST',
        body: JSON.stringify({
          phone: phone
        }),
      });
    },

    resendEmailOTP: async (email) => {
      return apiRequest(`${LEARN_EARN_API}/subscription/resend-email-otp`, {
        method: 'POST',
        body: JSON.stringify({
          email: email
        }),
      });
    },

    forgotPassword: async (forgotData) => {
      return apiRequest(`${LEARN_EARN_API}/subscription/forgot-password`, {
        method: 'POST',
        body: JSON.stringify({
          username: forgotData.username,
          email: forgotData.email
        }),
      });
    },

    verifyForgotPasswordOTP: async (otpData) => {
      return apiRequest(`${LEARN_EARN_API}/subscription/verify-forgot-password-otp`, {
        method: 'POST',
        body: JSON.stringify({
          username: otpData.username,
          email: otpData.email,
          otp: otpData.otp
        }),
      });
    },

    resetPassword: async (resetData) => {
      return apiRequest(`${LEARN_EARN_API}/subscription/reset-password`, {
        method: 'POST',
        body: JSON.stringify({
          username: resetData.username,
          email: resetData.email,
          otp: resetData.otp,
          newPassword: resetData.newPassword
        }),
      });
    },

    signIn: async (email, password) => {
      return apiRequest(`${LEARN_EARN_API}/subscription/signin`, {
        method: 'POST',
        body: JSON.stringify({
          email: email,
          password: password
        }),
      });
    },

    createSubscription: async (subscriptionData, token) => {
      return apiRequest(`${LEARN_EARN_API}/subscription/subscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: subscriptionData.userId,
          categoryId: subscriptionData.categoryId, // 'assignments', 'journals', 'combo'
          uploadType: subscriptionData.uploadType,
          planId: subscriptionData.planId, // '3m', '6m', '1y'
          paymentId: subscriptionData.paymentId
        }),
      });
    },

    uploadFile: async (file, workType, additionalInfo, token) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('workType', workType);
      formData.append('additionalInfo', additionalInfo);

      return apiRequest(`${LEARN_EARN_API}/subscription/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });
    },

    checkStatus: async (email) => {
      const params = new URLSearchParams({ email });
      return apiRequest(`${LEARN_EARN_API}/subscription/check-status?${params}`);
    },

    getPaymentStatus: async (id) => {
      return apiRequest(`${LEARN_EARN_API}/subscription/${id}/payment-status`);
    },
  },

  // Admin APIs - UPDATED
  admin: {
    getDashboard: async (token) => {
      return apiRequest(`${LEARN_EARN_API}/admin/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    },

    getAllSubmissions: async (page = 1, limit = 50, status = '', workType = '', token) => {
      const params = new URLSearchParams({ page, limit });
      if (status) params.append('status', status);
      if (workType) params.append('workType', workType);

      return apiRequest(`${LEARN_EARN_API}/admin/customer-work?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    },

    getAllApplications: async (page = 1, limit = 20, status = '', token) => {
      const params = new URLSearchParams({ page, limit });
      if (status) params.append('status', status);

      return apiRequest(`${LEARN_EARN_API}/admin/applications?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    },

    updateApplicationStatus: async (id, status, reviewNotes = '', reviewedBy = '', token) => {
      return apiRequest(`${LEARN_EARN_API}/admin/applications/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status, reviewNotes, reviewedBy }),
      });
    },

    generateReports: async (startDate, endDate, type = 'summary', token) => {
      const params = new URLSearchParams({ startDate, endDate, type });
      return apiRequest(`${LEARN_EARN_API}/admin/reports?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    },
  },
};

// Payment APIs - COMPLETELY NEW
export const paymentAPI = {
  createOrder: async (orderData) => {
    console.log('🚀 Payment Create Order API - Data being sent:', orderData);

    // Clean data - ensure proper types
    const cleanData = {
      amount: parseFloat(orderData.amount), // Amount in INR
      currency: orderData.currency || 'INR',
      serviceType: orderData.serviceType.toString(),
      serviceId: orderData.serviceId.toString(),
      serviceDescription: orderData.serviceDescription.toString(),
      customerName: orderData.customerName.toString(),
      customerEmail: orderData.customerEmail.toString(),
      customerPhone: orderData.customerPhone.toString(),
      notes: orderData.notes || {}
    };

    console.log('🧹 Clean payment order data being sent:', cleanData);

    try {
      const response = await fetch(`${API_BASE_URL}/api/payment/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cleanData)
      });

      console.log('📡 Payment create order response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Payment create order error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const responseData = await response.json();
      console.log('✅ Payment create order success response:', responseData);

      return {
        success: true,
        data: responseData,
        message: responseData.message
      };

    } catch (error) {
      console.error('❌ Payment Create Order API Error:', error);
      throw error;
    }
  },

  verifyPayment: async (paymentData) => {
    console.log('🚀 Payment Verify API - Data being sent:', paymentData);

    try {
      const response = await fetch(`${API_BASE_URL}/api/payment/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData)
      });

      console.log('📡 Payment verify response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Payment verify error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const responseData = await response.json();
      console.log('✅ Payment verify success response:', responseData);

      return {
        success: true,
        data: responseData,
        message: responseData.message
      };

    } catch (error) {
      console.error('❌ Payment Verify API Error:', error);
      throw error;
    }
  },

  getPaymentStatus: async (paymentId) => {
    return apiRequest(`/api/payment/status/${paymentId}`);
  },

  getOTSPaymentStatus: async (submissionId) => {
    return apiRequest(`${LEARN_EARN_API}/ots/${submissionId}/payment-status`);
  },

  getOTSPaymentHistory: async (email, page = 1, limit = 10) => {
    const params = new URLSearchParams({ email, page, limit });
    return apiRequest(`${LEARN_EARN_API}/ots/payment-history?${params}`);
  },
};

// Error handling utility
export const handleAPIError = (response) => {
  if (!response.success) {
    // Handle specific error codes
    switch (response.error?.code) {
      case 'VALIDATION_ERROR':
        return response.error.errors || [{ message: response.message }];
      case 'RATE_LIMIT_EXCEEDED':
        return [{ message: 'Too many requests. Please try again later.' }];
      case 'FILE_TOO_LARGE':
        return [{ message: 'File size too large. Maximum size is 10MB.' }];
      case 'WORK_ALREADY_TAKEN':
        return [{ message: 'Sorry! Someone else just applied for this work.' }];
      default:
        return [{ message: response.message || 'An error occurred' }];
    }
  }
  return null;
};

// Health check - updated endpoint
export const healthCheck = async () => {
  return apiRequest('/api/health');
};

// Export apiRequest for use in other utilities (like razorpayPayment.js)
export { apiRequest };

const api = {
  policyAPI,
  investmentAPI,
  contactAPI,
  learnEarnAPI,
  paymentAPI,
  handleAPIError,
  healthCheck,
};

export default api;